import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

function ImageCarousel({ images, open, onClose, initialIndex = 0 }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, currentIndex]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (!images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.95)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none',
          p: { xs: 2, md: 4 },
        }}
        onClick={(e) => {
          // Close when clicking on backdrop (not on image or controls)
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: { xs: 8, md: 16 },
            right: { xs: 8, md: 16 },
            color: '#fff',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(10px)',
            zIndex: 1300,
            '&:hover': {
              bgcolor: 'rgba(255, 107, 107, 0.8)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Previous Button */}
        {images.length > 1 && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: { xs: 8, md: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              zIndex: 1300,
              '&:hover': {
                bgcolor: 'rgba(78, 205, 196, 0.8)',
              },
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
        )}

        {/* Next Button */}
        {images.length > 1 && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: 8, md: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(10px)',
              zIndex: 1300,
              '&:hover': {
                bgcolor: 'rgba(78, 205, 196, 0.8)',
              },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        )}

        {/* Image */}
        <Box
          sx={{
            maxWidth: '90%',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            component="img"
            src={currentImage.src}
            alt={currentImage.title || currentImage.alt}
            sx={{
              maxWidth: '100%',
              maxHeight: '75vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            }}
          />

          {/* Image Info */}
          <Box
            sx={{
              textAlign: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(10px)',
              px: 3,
              py: 1.5,
              borderRadius: '8px',
              maxWidth: '100%',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#fff',
                fontWeight: 500,
              }}
            >
              {currentImage.title || currentImage.alt}
            </Typography>
            {images.length > 1 && (
              <Typography
                variant="caption"
                sx={{
                  color: '#a3a3a3',
                  display: 'block',
                  mt: 0.5,
                }}
              >
                {currentIndex + 1} / {images.length}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Thumbnails */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 8, md: 16 },
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(10px)',
              p: 1,
              borderRadius: '8px',
              maxWidth: '90%',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 107, 107, 0.5)',
                borderRadius: '4px',
              },
            }}
          >
            {images.map((img, idx) => (
              <Box
                key={idx}
                component="img"
                src={img.src}
                alt={img.title || img.alt}
                onClick={() => setCurrentIndex(idx)}
                sx={{
                  width: 60,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  opacity: idx === currentIndex ? 1 : 0.5,
                  border: idx === currentIndex ? '2px solid #ff6b6b' : '2px solid transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.1)',
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
}

export default ImageCarousel;

